import React, { useState, useMemo, useEffect } from "react";
import ReactDOM from "react-dom";
import { auth, db } from "../firebase";
import { useNotifications } from "../hooks/notificationsContext";
import calculatePortfolio from "../utils/calculatePortfolio";
import { doc, getDoc } from "firebase/firestore";

export default function SellSharesModal({ company, userData, onClose }) {
  const [globalTax, setGlobalTax] = useState(5);
  const [globalGoldPressure, setGlobalGoldPressure] = useState(1);
  const [regionPressureVars, setRegionPressureVars] = useState({});
  const [shares, setShares] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const { addNotification } = useNotifications();
  const [denomination, setDenomination] = useState("USD");

const allowedWallets = useMemo(() => {
  if (!company) return ["USD"];
  
  const ticker = String(company.companyTicker || "").toUpperCase().trim();
  const region = company.companyRegion;
  
  let walletSet = new Set(["USD"]);

  if (["AUD", "BRL", "CNY", "EUR", "ZAR"].includes(ticker)) {
    walletSet = new Set(["AUD", "BRL", "CNY", "EUR", "ZAR", "USD"]);
    walletSet.delete(ticker);
  } 
  else if (ticker === "XAU") {
    walletSet = new Set(["USD", "AUD", "BRL", "CNY", "EUR", "ZAR"]);
  } 
  else if (region && region !== "false") {
    const map = { AU: "AUD", SA: "BRL", AS: "CNY", EU: "EUR", AF: "ZAR", NA: "USD" };
    const regCur = map[region];
    if (regCur) {
      walletSet.add(regCur);
    }
  }

  return Array.from(walletSet);
}, [company]);

useEffect(() => {
  if (!allowedWallets.includes(denomination)) setDenomination("USD");
}, [allowedWallets, denomination]);

  useEffect(() => {
    const getGlobalData = async () => {
      try {
        const docRef = doc(db, "Global_Vars", "Main");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setGlobalTax(data.globalBaseTaxRate ?? 5);
          setGlobalGoldPressure(data.globalGoldPressure ?? 1);
          setRegionPressureVars(data || {});
        }
      } catch (err) {
        console.error("Error fetching global context metadata:", err);
      }
    };

    getGlobalData();
  }, []);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const basePrice = company?.companyStockPrice || 0;

  const instrumentType = useMemo(() => {
    if (company?.companyCrypto === true || String(company?.companyCrypto).toLowerCase() === "true") return "Crypto";
    if (company?.companyTicker === "XAU") return "Gold";
    if (["BND3", "BND7", "BND14"].includes(company?.companyTicker)) return "Bond";
    if (company?.isCurrency === true || String(company?.isCurrency).toLowerCase() === "true") return "Currency";
    return "Stock";
  }, [company]);

  const userHoldings = useMemo(() => {
    if (company?.companyTicker === "XAU") {
      return userData?.userGold || 0;
    }
    return userData?.[`company${company?.companyTicker}`] || 0;
  }, [company, userData]);

  const maxShares = Math.floor(userHoldings);

  const gpSell = useMemo(() => {
    if (!company || shares < 1) {
      return { avgPrice: basePrice, newPrice: basePrice, totalProceeds: 0 };
    }

    const companyRegion = company.companyRegion || "NONE";
    const liveRegionPressure = regionPressureVars[`regionPressure${companyRegion}`] ?? (company.regionPressure || 0);
    const liveGlobalGoldPressure = globalGoldPressure;

    const totalStock = company.companyStockVolume || 1;
    const availableRatio = Math.min(Math.max((company.companyOutstanding || 0) / totalStock, 0), 1);

    let perStepImpact = 0;

    if (instrumentType === "Crypto") {
      perStepImpact = (1 / totalStock) * availableRatio * (1 + liveRegionPressure / 100);
    } else if (instrumentType === "Currency") {
      const stabilityWeight = 50.0;
      perStepImpact = (1 / (totalStock * stabilityWeight)) * (1 + liveRegionPressure / 100);
    } else {
      perStepImpact = (1 / totalStock) * availableRatio * (1 + liveRegionPressure / 100) * liveGlobalGoldPressure;
    }

    let perStep = 1 / (1 + perStepImpact); 
    const MIN_R = instrumentType === "Currency" ? 0.0001 : 0.0001; 
    if (!isFinite(perStep) || perStep <= MIN_R) perStep = MIN_R;

    const MAX_TRADE_BLOCK = 100000;
    let remainingUnits = shares;
    let rawCalculatedCost = 0;
    let processingPrice = basePrice;
    const lowestFloorLimit = instrumentType === "Currency" ? 0.001 : 0.01;

    if (remainingUnits > MAX_TRADE_BLOCK) {
      while (remainingUnits > 0) {
        const currentBlockChunk = Math.min(remainingUnits, MAX_TRADE_BLOCK);
        const blockPowTerm = Math.pow(perStep, currentBlockChunk);

        let blockCost;
        if (Math.abs(perStep - 1) < 1e-12) {
          blockCost = processingPrice * currentBlockChunk;
        } else {
          blockCost = (processingPrice * (1 - blockPowTerm)) / (1 - perStep);
        }

        rawCalculatedCost += Math.abs(blockCost);
        processingPrice = Math.max(processingPrice * blockPowTerm, lowestFloorLimit);
        remainingUnits -= currentBlockChunk;

        if (!isFinite(processingPrice) || processingPrice < lowestFloorLimit) {
          processingPrice = lowestFloorLimit;
          break;
        }
      }
    } else {
      const powTerm = Math.pow(perStep, remainingUnits);
      if (Math.abs(perStep - 1) < 1e-12) {
        rawCalculatedCost = processingPrice * remainingUnits;
      } else {
        rawCalculatedCost = (processingPrice * (1 - powTerm)) / (1 - perStep);
      }
      processingPrice = Math.max(processingPrice * powTerm, lowestFloorLimit);
    }

    const totalProceeds = Math.abs(rawCalculatedCost);
    const newPrice = Math.min(Math.max(processingPrice, lowestFloorLimit), 100000000);
    const avgPrice = totalProceeds / shares;

    return { avgPrice, newPrice, totalProceeds };
  }, [company, shares, basePrice, instrumentType, globalGoldPressure, regionPressureVars]);

  const taxDetails = useMemo(() => {
    if (!company || !userData || shares < 1) return { finalRate: 0, regionalAdjustment: 0, label: "STANDARD" };

    const globalBaseTaxRate = globalTax;
    let taxMultiplier = 1;
    let label = "STANDARD";

    if (instrumentType === "Bond") taxMultiplier = 2;
    else if (instrumentType === "Crypto") taxMultiplier = 0.95;
    else if (instrumentType === "Gold") taxMultiplier = 1.05;

    const baseAssetMultiplier = taxMultiplier;
    const ur = userData.userRegion;
    const cr = company.companyRegion || "NONE";

    if (ur && ur !== "false" && instrumentType !== "Bond") {
      if (ur === "AF") {
        if (instrumentType === "Gold") { taxMultiplier -= 0.05; label = "AF-GOLD RESERVE"; }
        else if (["AS", "EU"].includes(cr)) { taxMultiplier -= 0.05; label = `AF-${cr} CORRIDOR`; }
      } else if (ur === "AS") {
        if (["AF", "AU", "SA"].includes(cr)) { taxMultiplier -= 0.05; label = `AS-${cr} TRADE`; }
        if (instrumentType === "Currency") { taxMultiplier -= 0.10; label = "AS-FOREX REBATE"; }
      } else if (ur === "AU") {
        if (["AS", "NA"].includes(cr)) { taxMultiplier -= 0.05; label = `AU-${cr} PACT`; }
        if (instrumentType === "Crypto") { taxMultiplier += 0.05; label = "AU-CRYPTO LEVY"; }
      } else if (ur === "EU") {
        taxMultiplier += 0.10; label = "EU SURCHARGE";
        if (["AF", "AU", "NA"].includes(cr)) { taxMultiplier -= 0.05; label = "EU-TRADE DISCOUNT"; }
      } else if (ur === "NA" && ["AU", "EU", "SA"].includes(cr)) {
        taxMultiplier -= 0.05; label = `NA-${cr} TREATY`;
      } else if (ur === "SA") {
        if (["AS", "NA"].includes(cr)) { taxMultiplier -= 0.05; label = `SA-${cr} ACCORD`; }
        if (instrumentType === "Crypto") { taxMultiplier -= 0.10; label = "SA-CRYPTO INCENTIVE"; }
        if (instrumentType === "Stock") { taxMultiplier += 0.05; label = "SA-EQUITY TAX"; }
      }
    }

    const finalRate = globalBaseTaxRate * taxMultiplier;
    const regionalAdjustment = (taxMultiplier - baseAssetMultiplier) * globalBaseTaxRate;

    return { finalRate, regionalAdjustment, label };
  }, [shares, company, userData, instrumentType, globalTax]);

  const taxes = gpSell.totalProceeds * (taxDetails.finalRate / 100);

  const userLeveragedHoldings = userData?.holderHoldingsLeveraged ?? (userData?.[`holderHoldingsLeveraged${company?.companyTicker}`] || 0);
  
  const netAfterTax = gpSell.totalProceeds - taxes;
  let leverageToRepay = Math.min(userLeveragedHoldings, netAfterTax);
  let finalPayout = Math.max(0, netAfterTax - leverageToRepay);

  if (finalPayout < 0) {
    leverageToRepay = netAfterTax;
    finalPayout = 0;
  }

  const handleSell = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch("https://transactionsell-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
body: JSON.stringify({
  ticker: company.companyTicker,
  quantity: shares,
  denomination: denomination
}),
      });

      const data = await res.json();

      if (data?.success) {
        addNotification(data.message, "success");
        onClose();
        await calculatePortfolio();
      } else {
        addNotification(data?.message || "Sale failed.", "error");
        setIsProcessing(false);
      }
    } catch (err) {
      console.error("Sell transaction error:", err);
      addNotification("Network error. Please try again.", "error");
      setIsProcessing(false);
    }
  };

  const unitLabel = instrumentType === "Crypto" ? "coins" : instrumentType === "Bond" ? "bonds" : instrumentType === "Gold" ? "gold units" : "shares";
  const sellSymbol = instrumentType === "Currency" ? (currencySymbols[company?.companyTicker] || "") : "";

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.8)", backdropFilter: "blur(5px)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, fontFamily: "'JetBrains Mono', monospace" }}>
      <div style={{ width: "420px", background: "rgba(0, 30, 30, 0.95)", border: "1px solid rgba(80, 227, 194, 0.3)", padding: "24px", position: "relative", boxShadow: "0 10px 50px rgba(0,0,0,0.8)" }}>
        
        <h2 style={{ color: "#fff", margin: "0 0 20px 0", fontSize: "1.1rem", borderBottom: "1px solid rgba(80, 227, 194, 0.2)", paddingBottom: "10px", letterSpacing: "1px" }}>
          SELL {company?.companyName?.toUpperCase()}
        </h2>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "20px" }}>
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", padding: "12px" }}>
            <div style={{ color: "#888", fontSize: "0.55rem" }}>OWNED ASSETS</div>
            <div style={{ color: "#eee", fontSize: "0.9rem" }}>{maxShares.toLocaleString()} <span style={{ fontSize: '0.6rem' }}>{unitLabel}</span></div>
          </div>
          <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", padding: "12px" }}>
            <div style={{ color: "#888", fontSize: "0.55rem" }}>MARKET PRICE</div>
            <div style={{ color: "#50e3c2", fontSize: "0.9rem" }}>{sellSymbol}{Number(basePrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ color: "#888", fontSize: "0.6rem", display: "block", marginBottom: "8px" }}>SELL QUANTITY</label>
          <input type="number" value={shares} onChange={(e) => setShares(Math.max(0, Math.min(maxShares, Number(e.target.value))))} style={{ width: "100%", background: "rgba(0,0,0,0.3)", border: "1px solid rgba(80, 227, 194, 0.2)", color: "#fff", padding: "10px", boxSizing: "border-box", outline: "none", fontFamily: "inherit" }} />
          <input type="range" min="0" max={maxShares || 0} value={shares} onChange={(e) => setShares(Number(e.target.value))} style={{ width: "100%", marginTop: "12px", accentColor: "#50e3c2" }} />
        </div>
<div style={{ marginBottom: "20px" }}>
  <label style={{ color: "#888", fontSize: "0.6rem", display: "block", marginBottom: "5px" }}>WALLET</label>
  {allowedWallets.length > 1 ? (
    <select
      value={denomination}
      onChange={(e) => setDenomination(e.target.value)}
      style={{
        width: "100%", background: "rgba(0, 30, 30, 0.95)", border: "1px solid rgba(80, 227, 194, 0.3)",
        color: "#50e3c2", padding: "9px", outline: "none", fontFamily: "inherit"
      }}
    >
      {allowedWallets.map((code) => (
        <option key={code} value={code}>{code}</option>
      ))}
    </select>
  ) : (
    <div style={{ 
      width: "95%", background: "rgba(255, 255, 255, 0.02)", border: "1px solid rgba(255, 255, 255, 0.05)", 
      color: "#50e3c2", padding: "10px", fontSize: "0.85rem" 
    }}>
      {denomination}
    </div>
  )}
</div>
        <div style={{ background: "rgba(0,0,0,0.3)", padding: "15px", marginBottom: "20px", border: "1px solid rgba(255,255,255,0.05)" }}>
           <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: '0.7rem' }}>
             <span style={{ color: "#888" }}>GROSS PROCEEDS:</span>
             <span style={{ color: "#eee" }}>{formatCurrency(gpSell.totalProceeds)}</span>
           </div>

           {taxDetails.regionalAdjustment !== 0 && (
             <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: '0.65rem' }}>
               <span style={{ color: "#666" }}>{taxDetails.label}:</span>
               <span style={{ color: taxDetails.regionalAdjustment < 0 ? "#50e3c2" : "#ff7b72" }}>
                 {taxDetails.regionalAdjustment > 0 ? "+" : ""}{taxDetails.regionalAdjustment.toFixed(2)}%
               </span>
             </div>
           )}

           <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: '0.7rem' }}>
             <span style={{ color: "#888" }}>TAX ({taxDetails.finalRate.toFixed(2)}%):</span>
             <span style={{ color: "#ff7b72" }}>-{formatCurrency(taxes)}</span>
           </div>

           {leverageToRepay > 0 && (
             <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: '0.7rem' }}>
               <span style={{ color: "#888" }}>DEBT REPAYMENT:</span>
               <span style={{ color: "#ff7b72" }}>-{formatCurrency(leverageToRepay)}</span>
             </div>
           )}

           <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", borderTop: '1px solid rgba(80, 227, 194, 0.3)', paddingTop: '10px', marginTop: '6px' }}>
             <span style={{ color: "#fff", fontSize: "0.65rem" }}>NET PAYOUT:</span>
             <span style={{ color: "#50e3c2", fontSize: "1.1rem" }}>{formatCurrency(finalPayout)}</span>
           </div>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button 
            onClick={onClose} 
            style={{ flex: 1, padding: "12px", background: "transparent", border: "1px solid #333", color: "#888", cursor: "pointer", fontFamily: "inherit" }}
          >
            CANCEL
          </button>
          
          <button 
            onClick={() => setShowConfirm(true)} 
            disabled={shares < 1 || maxShares === 0 || isProcessing} 
            style={{ 
              flex: 2, 
              padding: "12px", 
              background: (shares < 1 || maxShares === 0 || isProcessing) ? "transparent" : "#50e3c2", 
              border: (shares < 1 || maxShares === 0 || isProcessing) ? "1px solid #333" : "none", 
              color: (shares < 1 || maxShares === 0 || isProcessing) ? "#444" : "#000", 
              fontWeight: "bold", 
              cursor: (shares < 1 || maxShares === 0 || isProcessing) ? "not-allowed" : "pointer",
              opacity: isProcessing ? 0.6 : 1,
              fontFamily: "inherit" 
            }}
          >
            {isProcessing ? "PROCESSING..." : "SELL"}
          </button>
        </div>

        {showConfirm && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0, 20, 20, 0.98)", backdropFilter: 'blur(5px)', display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "24px", border: "2px solid #50e3c2", textAlign: "center", zIndex: 10 }}>
            <div style={{ color: "#fff", marginBottom: "20px", fontSize: "0.85rem", lineHeight: "1.6" }}>
              Selling <span style={{ color: '#50e3c2' }}>{shares.toLocaleString()}</span> {sellSymbol}{unitLabel}<br/> 
              Est. Net Payout:<br/>
              <span style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#50e3c2' }}>{formatCurrency(finalPayout)}</span>
            </div>
            
            <div style={{ display: "flex", gap: "10px", width: "100%" }}>
              <button 
                onClick={() => {
                  setShowConfirm(false);
                  setIsProcessing(false);
                }} 
                disabled={isProcessing}
                style={{ flex: 1, padding: "12px", background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid #333", fontFamily: "inherit", cursor: isProcessing ? "not-allowed" : "pointer" }}
              >
                CANCEL
              </button>
              
              <button 
                onClick={handleSell} 
                disabled={isProcessing}
                style={{ 
                  flex: 1, 
                  padding: "12px", 
                  background: isProcessing ? "#2a7a69" : "#50e3c2", 
                  color: "#000", 
                  border: "none", 
                  fontWeight: "bold", 
                  fontFamily: "inherit", 
                  cursor: isProcessing ? "not-allowed" : "pointer" 
                }}
              >
                {isProcessing ? "PROCESSING..." : "CONFIRM"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

const currencySymbols = {
  USD: "$",
  AUD: "A$",
  BRL: "R$",
  CNY: "¥",
  EUR: "€",
  ZAR: "R",
};